import React, { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Search, Plus, RefreshCw, Phone, Mail, MapPin, 
  ShoppingBag, DollarSign, Clock, Edit, Trash2, Eye,
  ChevronRight, ChevronLeft, Download, Upload, User, Building2, Package,
  CheckCircle, XCircle, AlertCircle, ChevronsLeft, ChevronsRight, ChevronDown,
  History, MessageSquare, Calendar, Link as LinkIcon, ExternalLink, CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import BrowserPushCard from '@/components/BrowserPushCard';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Common country codes for UAE business (sorted by relevance)
const COUNTRY_CODES = [
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+968', country: 'Oman', flag: '🇴🇲' },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭' },
  { code: '+974', country: 'Qatar', flag: '🇶🇦' },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+92', country: 'Pakistan', flag: '🇵🇰' },
  { code: '+63', country: 'Philippines', flag: '🇵🇭' },
  { code: '+20', country: 'Egypt', flag: '🇪🇬' },
  { code: '+962', country: 'Jordan', flag: '🇯🇴' },
  { code: '+961', country: 'Lebanon', flag: '🇱🇧' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
];

// Phone input component with country code selector
const PhoneInput = ({ value, onChange, placeholder = "501234567", className = "" }) => {
  const [countryCode, setCountryCode] = useState('+971');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Parse existing value to extract country code and number
  useEffect(() => {
    if (value) {
      const found = COUNTRY_CODES.find(c => value.startsWith(c.code));
      if (found) {
        setCountryCode(found.code);
        setPhoneNumber(value.substring(found.code.length).trim());
      } else if (value.startsWith('+')) {
        // Try to extract unknown country code
        const match = value.match(/^(\+\d{1,4})\s*(.*)/);
        if (match) {
          setCountryCode(match[1]);
          setPhoneNumber(match[2]);
        }
      } else {
        setPhoneNumber(value);
      }
    }
  }, []);
  
  const handleCountrySelect = (code) => {
    setCountryCode(code);
    setIsOpen(false);
    setSearchTerm('');
    onChange(code + phoneNumber);
  };
  
  const handlePhoneChange = (e) => {
    const num = e.target.value.replace(/[^\d]/g, ''); // Only digits
    setPhoneNumber(num);
    onChange(countryCode + num);
  };
  
  const filteredCountries = COUNTRY_CODES.filter(c => 
    c.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.code.includes(searchTerm)
  );
  
  const selectedCountry = COUNTRY_CODES.find(c => c.code === countryCode) || { flag: '🌍', code: countryCode };
  
  return (
    <div className={`flex ${className}`}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="flex items-center gap-1 px-2 rounded-r-none border-r-0 bg-wt-bg border-wt-border text-wt-text-primary hover:bg-wt-paper min-w-[90px]"
            data-testid="country-code-selector"
          >
            <span className="text-base">{selectedCountry.flag}</span>
            <span className="text-xs font-mono">{countryCode}</span>
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[220px] p-2 bg-wt-paper border-wt-border" align="start">
          <Input
            placeholder="Search country..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-2 bg-wt-bg border-wt-border text-wt-text-primary text-sm h-8"
            autoFocus
          />
          <div className="max-h-[200px] overflow-y-auto space-y-0.5">
            {filteredCountries.map((c) => (
              <button
                key={c.code}
                onClick={() => handleCountrySelect(c.code)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-wt-gold/10 transition-colors ${
                  countryCode === c.code ? 'bg-wt-gold/20 text-wt-gold' : 'text-wt-text-primary'
                }`}
              >
                <span className="text-base">{c.flag}</span>
                <span className="font-mono text-xs">{c.code}</span>
                <span className="text-wt-text-muted text-xs truncate">{c.country}</span>
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <Input
        type="tel"
        value={phoneNumber}
        onChange={handlePhoneChange}
        placeholder={placeholder}
        className="rounded-l-none bg-wt-bg border-wt-border text-wt-text-primary flex-1"
        data-testid="phone-number-input"
      />
    </div>
  );
};

const CustomersPage = () => {
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  
  // State
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [customerActivity, setCustomerActivity] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activityDays, setActivityDays] = useState(30);
  const [expandedDates, setExpandedDates] = useState({});
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const ITEMS_PER_PAGE = 30;
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    notes: '',
    sync_to_shopify: true,
    addresses: []
  });
  
  const [newAddress, setNewAddress] = useState({
    address1: '',
    address2: '',
    city: '',
    province: '',
    country: 'UAE',
    zip: '',
    phone: '',
    is_default: false
  });

  // Load customers with pagination
  const loadCustomers = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = { 
        page: page,
        limit: ITEMS_PER_PAGE
      };
      // When searching, don't use pagination - backend returns all matching results
      if (searchQuery) {
        params.search = searchQuery;
        delete params.page;
      }
      
      const response = await axios.get(`${API_BASE_URL}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setCustomers(response.data.customers || []);
      setTotalCustomers(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
      setHasNext(response.data.has_next || false);
      setHasPrevious(response.data.has_previous || false);
      setCurrentPage(response.data.page || 1);
    } catch (error) {
      toast.error('Failed to load customers');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  // Load on mount and when search changes
  useEffect(() => {
    const debounce = setTimeout(() => {
      // Reset to page 1 when search changes
      setCurrentPage(1);
      loadCustomers(1);
    }, 300);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Load when page changes (not from search)
  useEffect(() => {
    if (!searchQuery && currentPage > 0) {
      loadCustomers(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Auto-open a customer when navigated to with `?customer_id=` /
  // `?email=` / `?phone=` (e.g. clicking the customer name on an order).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cid = params.get('customer_id');
    const email = params.get('email');
    const phone = params.get('phone');
    if (!cid && !email && !phone) return;
    (async () => {
      try {
        if (cid) {
          await loadCustomerDetails(cid);
          return;
        }
        // Fall back to email/phone — search the list and open the match.
        const token = localStorage.getItem('token');
        const q = email || phone;
        const response = await axios.get(
          `${API_BASE_URL}/customers?search=${encodeURIComponent(q)}&limit=1`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const match = (response.data?.customers || response.data || [])[0];
        if (match?.id) {
          await loadCustomerDetails(match.id);
        } else {
          toast.error('No matching customer found');
        }
      } catch {
        toast.error('Failed to open linked customer');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pagination handlers
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Sync from Shopify
  const handleSyncFromShopify = async () => {
    try {
      setSyncing(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/customers/sync-from-shopify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Synced ${response.data.total_synced} customers (${response.data.created} new, ${response.data.updated} updated)`);
      setCurrentPage(1);
      loadCustomers(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync customers');
    } finally {
      setSyncing(false);
    }
  };

  // Sync store credits from Shopify — runs in BACKGROUND on the backend so
  // large tenants don't time out. UI polls job status every 2s.
  const [syncingCredits, setSyncingCredits] = useState(false);
  const [creditSyncJob, setCreditSyncJob] = useState(null);
  const handleSyncStoreCredits = async () => {
    try {
      setSyncingCredits(true);
      setCreditSyncJob(null);
      const token = localStorage.getItem('token');
      const start = await axios.post(`${API_BASE_URL}/customers/sync-store-credits`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const jobId = start.data.job_id;
      toast.info('Store credit sync started in the background…');

      // Poll progress
      let lastStats = null;
      const poll = async () => {
        try {
          const r = await axios.get(`${API_BASE_URL}/customers/store-credit-sync-jobs/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCreditSyncJob(r.data);
          lastStats = r.data.stats || lastStats;
          if (r.data.status === 'completed') {
            const s = r.data.stats || {};
            toast.success(`Store credits synced: ${s.customers_updated || 0} updated · ${(s.credits_added_triggers || 0) + (s.credits_removed_triggers || 0)} journey events fired`);
            loadCustomers(currentPage);
            setSyncingCredits(false);
            return;
          }
          if (r.data.status === 'failed') {
            toast.error(`Sync failed: ${r.data.error || 'Unknown error'}`);
            setSyncingCredits(false);
            return;
          }
          setTimeout(poll, 2000);
        } catch (_e) {
          toast.error('Lost progress tracking; check back later.');
          setSyncingCredits(false);
        }
      };
      setTimeout(poll, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start store credit sync');
      setSyncingCredits(false);
    }
  };

  // Fix missing phone numbers from addresses
  const [migratingPhones, setMigratingPhones] = useState(false);
  const handleMigratePhones = async () => {
    try {
      setMigratingPhones(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/customers/migrate-phone-numbers`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Updated ${response.data.customers_updated} customers with phone numbers from addresses`);
      if (response.data.customers_updated > 0) {
        loadCustomers(currentPage);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to migrate phone numbers');
    } finally {
      setMigratingPhones(false);
    }
  };

  // Load customer details with orders
  const loadCustomerDetails = async (customerId) => {
    try {
      setLoadingOrders(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedCustomer(response.data);
      setCustomerOrders(response.data.orders || []);
      setDetailDialogOpen(true);
      // Also load activity
      loadCustomerActivity(customerId);
    } catch (error) {
      toast.error('Failed to load customer details');
    } finally {
      setLoadingOrders(false);
    }
  };

  // Load customer activity timeline
  const loadCustomerActivity = async (customerId, days = 30) => {
    try {
      setLoadingActivity(true);
      setExpandedDates({});
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/customers/${customerId}/activity?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerActivity(response.data);
    } catch (error) {
      console.error('Failed to load activity:', error);
      setCustomerActivity(null);
    } finally {
      setLoadingActivity(false);
    }
  };

  // Toggle expanded date in activity timeline
  const toggleDateExpanded = (date) => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  // Create customer
  const handleCreateCustomer = async () => {
    if (!formData.first_name || !formData.phone) {
      toast.error('Name and phone are required');
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/customers`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Customer created successfully');
      setCreateDialogOpen(false);
      resetForm();
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    } finally {
      setSaving(false);
    }
  };

  // Update customer
  const handleUpdateCustomer = async () => {
    if (!selectedCustomer) return;
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/customers/${selectedCustomer.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Customer updated successfully');
      setEditDialogOpen(false);
      resetForm();
      loadCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  // Delete customer
  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Customer deleted');
      loadCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      notes: '',
      sync_to_shopify: true,
      addresses: []
    });
    setNewAddress({
      address1: '',
      address2: '',
      city: '',
      province: '',
      country: 'UAE',
      zip: '',
      phone: '',
      is_default: false
    });
  };

  // Open edit dialog
  const openEditDialog = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      first_name: customer.first_name || '',
      last_name: customer.last_name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      notes: customer.notes || '',
      sync_to_shopify: true,
      addresses: customer.addresses || []
    });
    setEditDialogOpen(true);
  };

  // Add address to form
  const addAddress = () => {
    if (!newAddress.address1 || !newAddress.city) {
      toast.error('Address and city are required');
      return;
    }
    setFormData(prev => ({
      ...prev,
      addresses: [...prev.addresses, { ...newAddress, id: Date.now().toString() }]
    }));
    setNewAddress({
      address1: '',
      address2: '',
      city: '',
      province: '',
      country: 'UAE',
      zip: '',
      phone: '',
      is_default: false
    });
  };

  // Remove address
  const removeAddress = (index) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.filter((_, i) => i !== index)
    }));
  };

  // Get status badge for order
  const getOrderStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending', className: 'bg-yellow-500/20 text-yellow-400' },
      accepted: { label: 'Accepted', className: 'bg-blue-500/20 text-blue-400' },
      delivered: { label: 'Delivered', className: 'bg-green-500/20 text-green-400' },
      fulfilled: { label: 'Fulfilled', className: 'bg-green-500/20 text-green-400' },
      failed: { label: 'Failed', className: 'bg-red-500/20 text-red-400' },
      cancelled: { label: 'Cancelled', className: 'bg-gray-500/20 text-gray-400' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-500/20 text-gray-400' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl font-bold text-wt-text-primary flex items-center gap-2">
            <Users className="h-6 w-6 text-wt-gold" />
            Customers
          </h1>
          <p className="text-wt-text-muted text-sm mt-1 hidden sm:block">
            Manage your customers and view their order history
          </p>
        </div>
        
        {/* Action buttons - scrollable on mobile */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible md:flex-wrap">
          <Button
            variant="outline"
            onClick={handleMigratePhones}
            disabled={migratingPhones}
            className="border-wt-border text-wt-text-primary hover:bg-wt-subtle whitespace-nowrap flex-shrink-0"
            data-testid="migrate-phones-btn"
            title="Fill missing phone numbers from customer addresses"
          >
            {migratingPhones ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Phone className="h-4 w-4 mr-2" />
            )}
            <span className="hidden sm:inline">Fix Missing Phones</span>
            <span className="sm:hidden">Fix Phones</span>
          </Button>
          
          <Button
            variant="outline"
            onClick={handleSyncFromShopify}
            disabled={syncing}
            className="border-wt-border text-wt-text-primary hover:bg-wt-subtle whitespace-nowrap flex-shrink-0"
            data-testid="sync-shopify-btn"
          >
            {syncing ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            <span className="hidden sm:inline">Sync from Shopify</span>
            <span className="sm:hidden">Sync</span>
          </Button>

          <Button
            variant="outline"
            onClick={handleSyncStoreCredits}
            disabled={syncingCredits}
            className="border-wt-border text-wt-text-primary hover:bg-wt-subtle whitespace-nowrap flex-shrink-0"
            data-testid="sync-credits-btn"
          >
            {syncingCredits ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <DollarSign className="h-4 w-4 mr-2" />
            )}
            <span className="hidden sm:inline">
              {syncingCredits && creditSyncJob?.stats
                ? `Syncing… ${creditSyncJob.stats.customers_updated || 0} updated`
                : 'Sync Store Credits'}
            </span>
            <span className="sm:hidden">Credits</span>
          </Button>
          
          <Button
            onClick={() => { resetForm(); setCreateDialogOpen(true); }}
            className="bg-wt-gold hover:bg-wt-gold-hover text-wt-bg whitespace-nowrap flex-shrink-0"
            data-testid="new-customer-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">New Customer</span>
            <span className="sm:hidden">New</span>
          </Button>
        </div>
      </div>

      {/* Stats & Search Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-wt-bg border-wt-border text-wt-text-primary"
            data-testid="customer-search"
          />
        </div>
        {!searchQuery && (
          <div className="text-sm text-wt-text-muted">
            Showing {customers.length} of {totalCustomers} customers
          </div>
        )}
        {searchQuery && customers.length > 0 && (
          <div className="text-sm text-wt-text-muted">
            Found {customers.length} matching customers
          </div>
        )}
      </div>

      {/* Customer Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-wt-gold" />
        </div>
      ) : customers.length === 0 ? (
        <div className="text-center py-12 bg-wt-paper rounded-lg border border-wt-border">
          <Users className="h-12 w-12 text-wt-text-muted mx-auto mb-4" />
          <p className="text-wt-text-muted">
            {searchQuery ? 'No customers found matching your search' : 'No customers yet. Sync from Shopify or create a new customer.'}
          </p>
        </div>
      ) : (
        <>
          {/* Table Layout */}
          <div className="bg-wt-paper rounded-lg border border-wt-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-wt-bg border-b border-wt-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-wt-text-muted">Customer</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-wt-text-muted hidden sm:table-cell">Contact</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-wt-text-muted hidden lg:table-cell">Location</th>
                    <th className="text-center px-4 py-3 text-sm font-medium text-wt-text-muted hidden sm:table-cell">Orders</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-wt-text-muted">Spent</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-wt-text-muted w-20">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-wt-border">
                  {customers.map((customer) => (
                    <tr 
                      key={customer.id} 
                      className="hover:bg-wt-subtle/50 transition-colors"
                      data-testid={`customer-row-${customer.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-wt-gold/20 flex items-center justify-center flex-shrink-0">
                            <User className="h-4 w-4 text-wt-gold" />
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-wt-text-primary truncate max-w-[120px] sm:max-w-none">
                              {customer.full_name || `${customer.first_name} ${customer.last_name}`.trim() || 'Unnamed'}
                            </div>
                            {/* Show phone on mobile under name */}
                            <div className="sm:hidden text-xs text-wt-text-muted truncate">
                              {customer.phone || customer.email || ''}
                            </div>
                            {customer.shopify_customer_id && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-green-500/50 text-green-400">
                                Shopify
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <div className="space-y-1 text-sm">
                          {customer.phone && (
                            <div className="flex items-center gap-1.5 text-wt-text-muted">
                              <Phone className="h-3 w-3" />
                              <span className="truncate max-w-[150px]">{customer.phone}</span>
                            </div>
                          )}
                          {customer.email && (
                            <div className="flex items-center gap-1.5 text-wt-text-muted">
                              <Mail className="h-3 w-3" />
                              <span className="truncate max-w-[150px]">{customer.email}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        {customer.addresses?.[0] ? (
                          <div className="flex items-center gap-1.5 text-sm text-wt-text-muted">
                            <MapPin className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate max-w-[180px]">
                              {customer.addresses[0].city}{customer.addresses[0].country ? `, ${customer.addresses[0].country}` : ''}
                            </span>
                          </div>
                        ) : (
                          <span className="text-wt-text-muted/50 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center hidden sm:table-cell">
                        <span className="inline-flex items-center gap-1 text-sm">
                          <ShoppingBag className="h-3.5 w-3.5 text-wt-gold" />
                          <span className="text-wt-text-primary font-medium">{customer.orders_count || 0}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-wt-text-primary font-medium text-sm">
                          {formatCurrency(customer.total_spent || 0)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => loadCustomerDetails(customer.id)}
                            className="h-8 w-8 p-0 text-wt-text-muted hover:text-wt-gold"
                            data-testid={`view-customer-${customer.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(customer)}
                            className="h-8 w-8 p-0 text-wt-text-muted hover:text-wt-gold hidden sm:flex"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCustomer(customer.id)}
                            className="h-8 w-8 p-0 text-wt-text-muted hover:text-red-400 hidden sm:flex"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls - only show when not searching */}
          {!searchQuery && totalPages > 1 && (
            <div className="flex items-center justify-between px-2">
              <div className="text-sm text-wt-text-muted">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(1)}
                  disabled={!hasPrevious}
                  className="h-8 w-8 p-0 border-wt-border text-wt-text-muted hover:text-wt-text-primary disabled:opacity-30"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={!hasPrevious}
                  className="h-8 w-8 p-0 border-wt-border text-wt-text-muted hover:text-wt-text-primary disabled:opacity-30"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                {/* Page number buttons */}
                <div className="flex items-center gap-1 mx-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => goToPage(pageNum)}
                        className={`h-8 w-8 p-0 ${
                          currentPage === pageNum 
                            ? 'bg-wt-gold text-wt-bg hover:bg-wt-gold-hover' 
                            : 'border-wt-border text-wt-text-muted hover:text-wt-text-primary'
                        }`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={!hasNext}
                  className="h-8 w-8 p-0 border-wt-border text-wt-text-muted hover:text-wt-text-primary disabled:opacity-30"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(totalPages)}
                  disabled={!hasNext}
                  className="h-8 w-8 p-0 border-wt-border text-wt-text-muted hover:text-wt-text-primary disabled:opacity-30"
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Customer Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Plus className="h-5 w-5 text-wt-gold" />
              New Customer
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="John"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary">Last Name</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Doe"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Phone *</Label>
                <PhoneInput
                  value={formData.phone}
                  onChange={(phone) => setFormData(prev => ({ ...prev, phone }))}
                  placeholder="501234567"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary">Email (Optional)</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="john@example.com"
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-wt-text-primary">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional notes..."
                className="bg-wt-bg border-wt-border text-wt-text-primary"
                rows={2}
              />
            </div>
            
            {/* Addresses Section */}
            <div className="border border-wt-border rounded-lg p-4">
              <h4 className="text-wt-text-primary font-medium mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-wt-gold" />
                Addresses
              </h4>
              
              {formData.addresses.length > 0 && (
                <div className="space-y-2 mb-4">
                  {formData.addresses.map((addr, index) => (
                    <div key={addr.id || index} className="flex items-start justify-between bg-wt-bg rounded p-2">
                      <div className="text-sm text-wt-text-muted">
                        {addr.address1}, {addr.city}, {addr.country}
                        {addr.is_default && <Badge className="ml-2 bg-wt-gold/20 text-wt-gold">Default</Badge>}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAddress(index)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Address Line 1"
                  value={newAddress.address1}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, address1: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                />
                <Input
                  placeholder="Address Line 2"
                  value={newAddress.address2}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, address2: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                />
                <Input
                  placeholder="City *"
                  value={newAddress.city}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, city: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                />
                <Input
                  placeholder="State/Province"
                  value={newAddress.province}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, province: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                />
                <Input
                  placeholder="Country"
                  value={newAddress.country}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, country: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                />
                <Input
                  placeholder="ZIP/Postal Code"
                  value={newAddress.zip}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, zip: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <label className="flex items-center gap-2 text-sm text-wt-text-muted">
                  <input
                    type="checkbox"
                    checked={newAddress.is_default}
                    onChange={(e) => setNewAddress(prev => ({ ...prev, is_default: e.target.checked }))}
                    className="rounded border-wt-border"
                  />
                  Set as default address
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addAddress}
                  className="border-wt-gold text-wt-gold hover:bg-wt-gold/10"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Address
                </Button>
              </div>
            </div>
            
            <label className="flex items-center gap-2 text-sm text-wt-text-muted">
              <input
                type="checkbox"
                checked={formData.sync_to_shopify}
                onChange={(e) => setFormData(prev => ({ ...prev, sync_to_shopify: e.target.checked }))}
                className="rounded border-wt-border"
              />
              Sync customer to Shopify
            </label>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              className="border-wt-border text-wt-text-primary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateCustomer}
              disabled={saving}
              className="bg-wt-gold hover:bg-wt-gold-hover text-wt-bg"
            >
              {saving ? 'Creating...' : 'Create Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <Edit className="h-5 w-5 text-wt-gold" />
              Edit Customer
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary">Last Name</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Phone *</Label>
                <PhoneInput
                  value={formData.phone}
                  onChange={(phone) => setFormData(prev => ({ ...prev, phone }))}
                  placeholder="501234567"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary">Email (Optional)</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-wt-text-primary">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-wt-bg border-wt-border text-wt-text-primary"
                rows={2}
              />
            </div>
            
            {/* Addresses Section - same as create */}
            <div className="border border-wt-border rounded-lg p-4">
              <h4 className="text-wt-text-primary font-medium mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-wt-gold" />
                Addresses
              </h4>
              
              {formData.addresses.length > 0 && (
                <div className="space-y-2 mb-4">
                  {formData.addresses.map((addr, index) => (
                    <div key={addr.id || index} className="flex items-start justify-between bg-wt-bg rounded p-2">
                      <div className="text-sm text-wt-text-muted">
                        {addr.address1}, {addr.city}, {addr.country}
                        {addr.is_default && <Badge className="ml-2 bg-wt-gold/20 text-wt-gold">Default</Badge>}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAddress(index)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-2">
                <Input
                  placeholder="Address Line 1"
                  value={newAddress.address1}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, address1: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                />
                <Input
                  placeholder="Address Line 2"
                  value={newAddress.address2}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, address2: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                />
                <Input
                  placeholder="City *"
                  value={newAddress.city}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, city: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                />
                <Input
                  placeholder="State/Province"
                  value={newAddress.province}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, province: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                />
                <Input
                  placeholder="Country"
                  value={newAddress.country}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, country: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                />
                <Input
                  placeholder="ZIP/Postal Code"
                  value={newAddress.zip}
                  onChange={(e) => setNewAddress(prev => ({ ...prev, zip: e.target.value }))}
                  className="bg-wt-bg border-wt-border text-wt-text-primary text-sm"
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <label className="flex items-center gap-2 text-sm text-wt-text-muted">
                  <input
                    type="checkbox"
                    checked={newAddress.is_default}
                    onChange={(e) => setNewAddress(prev => ({ ...prev, is_default: e.target.checked }))}
                    className="rounded border-wt-border"
                  />
                  Set as default address
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addAddress}
                  className="border-wt-gold text-wt-gold hover:bg-wt-gold/10"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Address
                </Button>
              </div>
            </div>
            
            <label className="flex items-center gap-2 text-sm text-wt-text-muted">
              <input
                type="checkbox"
                checked={formData.sync_to_shopify}
                onChange={(e) => setFormData(prev => ({ ...prev, sync_to_shopify: e.target.checked }))}
                className="rounded border-wt-border"
              />
              Sync changes to Shopify
            </label>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="border-wt-border text-wt-text-primary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateCustomer}
              disabled={saving}
              className="bg-wt-gold hover:bg-wt-gold-hover text-wt-bg"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <User className="h-5 w-5 text-wt-gold" />
              {selectedCustomer?.full_name || 'Customer Details'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCustomer && (
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="bg-wt-bg border border-wt-border w-full justify-start">
                <TabsTrigger value="info" className="data-[state=active]:bg-wt-gold data-[state=active]:text-wt-bg">
                  Info
                </TabsTrigger>
                <TabsTrigger value="addresses" className="data-[state=active]:bg-wt-gold data-[state=active]:text-wt-bg">
                  Addresses ({selectedCustomer.addresses?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="orders" className="data-[state=active]:bg-wt-gold data-[state=active]:text-wt-bg">
                  Orders ({customerOrders.length})
                </TabsTrigger>
                <TabsTrigger value="activity" className="data-[state=active]:bg-wt-gold data-[state=active]:text-wt-bg">
                  <History className="h-4 w-4 mr-1" />
                  Activity
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-wt-bg rounded-lg p-4">
                    <p className="text-wt-text-muted text-sm">Phone</p>
                    <p className="text-wt-text-primary font-medium flex items-center gap-2">
                      <Phone className="h-4 w-4 text-wt-gold" />
                      {selectedCustomer.phone || 'Not provided'}
                    </p>
                  </div>
                  <div className="bg-wt-bg rounded-lg p-4">
                    <p className="text-wt-text-muted text-sm">Email</p>
                    <p className="text-wt-text-primary font-medium flex items-center gap-2">
                      <Mail className="h-4 w-4 text-wt-gold" />
                      {selectedCustomer.email || 'Not provided'}
                    </p>
                  </div>
                  <div className="bg-wt-bg rounded-lg p-4">
                    <p className="text-wt-text-muted text-sm">Total Orders</p>
                    <p className="text-wt-text-primary font-medium flex items-center gap-2">
                      <ShoppingBag className="h-4 w-4 text-wt-gold" />
                      {selectedCustomer.orders_count || customerOrders.length}
                    </p>
                  </div>
                  <div className="bg-wt-bg rounded-lg p-4">
                    <p className="text-wt-text-muted text-sm">Total Spent</p>
                    <p className="text-wt-text-primary font-medium flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-green-400" />
                      {formatCurrency(selectedCustomer.total_spent || 0)}
                    </p>
                  </div>
                  {/* Store Credits - Always show */}
                  <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/5 rounded-lg p-4 border border-emerald-500/20">
                    <p className="text-emerald-400 text-sm font-medium flex items-center gap-1">
                      <CreditCard className="h-4 w-4" />
                      Store Credits
                    </p>
                    <p className="text-2xl font-bold text-emerald-400 mt-1">
                      {formatCurrency(selectedCustomer.store_credit_balance || 0)}
                    </p>
                    {selectedCustomer.next_credit_expiry_at && (
                      <p className="text-xs text-amber-400 mt-1" data-testid="next-credit-expiry">
                        Next expiry: {new Date(selectedCustomer.next_credit_expiry_at).toLocaleDateString()}
                      </p>
                    )}
                    {selectedCustomer.store_credits?.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {selectedCustomer.store_credits.map((credit, idx) => (
                          <div key={idx} className="text-xs bg-wt-bg/50 rounded px-2 py-1 flex justify-between">
                            <span className="text-wt-text-muted">
                              {credit.note || `Credit #${idx + 1}`}
                            </span>
                            <div className="text-right">
                              <span className="text-emerald-400">{formatCurrency(credit.amount || 0)}</span>
                              {credit.expires_at && (
                                <span className="text-amber-400 ml-2">
                                  Exp: {new Date(credit.expires_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {!selectedCustomer.store_credits?.length && selectedCustomer.store_credit_balance === 0 && (
                      <p className="text-xs text-wt-text-muted mt-1">No store credits</p>
                    )}
                  </div>

                  {/* Browser Push channel reachability */}
                  <BrowserPushCard customerId={selectedCustomer.id} />
                </div>
                
                {selectedCustomer.notes && (
                  <div className="bg-wt-bg rounded-lg p-4">
                    <p className="text-wt-text-muted text-sm mb-1">Notes</p>
                    <p className="text-wt-text-primary">{selectedCustomer.notes}</p>
                  </div>
                )}
                
                {selectedCustomer.shopify_customer_id && (
                  <div className="flex items-center gap-2 text-sm text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    Synced with Shopify (ID: {selectedCustomer.shopify_customer_id})
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="addresses" className="mt-4">
                {selectedCustomer.addresses?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedCustomer.addresses.map((addr, index) => (
                      <div key={addr.id || index} className="bg-wt-bg rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-wt-text-primary font-medium">
                              {addr.address1}
                              {addr.address2 && `, ${addr.address2}`}
                            </p>
                            <p className="text-wt-text-muted text-sm">
                              {addr.city}{addr.province && `, ${addr.province}`}
                            </p>
                            <p className="text-wt-text-muted text-sm">
                              {addr.country} {addr.zip}
                            </p>
                            {addr.phone && (
                              <p className="text-wt-text-muted text-sm mt-1 flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {addr.phone}
                              </p>
                            )}
                          </div>
                          {addr.is_default && (
                            <Badge className="bg-wt-gold/20 text-wt-gold">Default</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-wt-text-muted text-center py-8">No addresses saved</p>
                )}
              </TabsContent>
              
              <TabsContent value="orders" className="mt-4">
                {loadingOrders ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-wt-gold" />
                  </div>
                ) : customerOrders.length > 0 ? (
                  <div className="space-y-3">
                    {customerOrders.map((order) => (
                      <div key={order.id} className="bg-wt-bg rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <Package className="h-5 w-5 text-wt-gold" />
                            <div>
                              <p className="text-wt-text-primary font-medium">
                                #{order.order_number || order.shopify_order_id}
                              </p>
                              <p className="text-wt-text-muted text-xs">
                                {new Date(order.created_at).toLocaleDateString('en-GB', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-wt-text-primary font-medium">
                              {formatCurrency(order.total_amount || order.total_price || 0)}
                            </p>
                            {getOrderStatusBadge(order.status)}
                          </div>
                        </div>
                        
                        {order.line_items && (
                          <div className="mt-2 pt-2 border-t border-wt-border">
                            <p className="text-wt-text-muted text-xs mb-1">
                              {order.line_items.length} item(s)
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {order.line_items.slice(0, 3).map((item, i) => (
                                <Badge key={i} variant="outline" className="text-xs border-wt-border text-wt-text-muted">
                                  {item.title?.substring(0, 30)}...
                                </Badge>
                              ))}
                              {order.line_items.length > 3 && (
                                <Badge variant="outline" className="text-xs border-wt-border text-wt-text-muted">
                                  +{order.line_items.length - 3} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-wt-text-muted text-center py-8">No orders yet</p>
                )}
              </TabsContent>
              
              {/* Activity Timeline Tab */}
              <TabsContent value="activity" className="mt-4">
                {/* Header with period selector */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-wt-text-muted">
                    <History className="h-5 w-5 text-blue-400" />
                    <span className="font-medium text-wt-text-primary">User Activity Timeline</span>
                  </div>
                  <select
                    value={activityDays}
                    onChange={(e) => {
                      setActivityDays(parseInt(e.target.value));
                      loadCustomerActivity(selectedCustomer.id, parseInt(e.target.value));
                    }}
                    className="bg-wt-bg border border-wt-border rounded-lg px-3 py-1.5 text-sm text-wt-text-primary"
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={365}>Last year</option>
                  </select>
                </div>
                
                {loadingActivity ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin text-wt-gold" />
                  </div>
                ) : customerActivity?.events_by_date?.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-center text-wt-text-muted text-sm mb-4">
                      {customerActivity.total_events} events found
                    </p>
                    
                    {customerActivity.events_by_date.map((dayGroup) => (
                      <div key={dayGroup.date} className="bg-wt-bg rounded-lg overflow-hidden">
                        {/* Date header - clickable to expand */}
                        <button
                          onClick={() => toggleDateExpanded(dayGroup.date)}
                          className="w-full flex items-center justify-between p-4 hover:bg-wt-bg/80 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <Calendar className="h-5 w-5 text-blue-400" />
                              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0.5 h-4 bg-blue-400/30" />
                            </div>
                            <div className="text-left">
                              <p className="text-wt-text-primary font-medium">{dayGroup.date_display}</p>
                              <p className="text-wt-text-muted text-xs">{dayGroup.events.length} event{dayGroup.events.length > 1 ? 's' : ''}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-wt-text-muted text-sm">
                            <span>Show events</span>
                            <ChevronDown className={`h-4 w-4 transition-transform ${expandedDates[dayGroup.date] ? 'rotate-180' : ''}`} />
                          </div>
                        </button>
                        
                        {/* Expanded events */}
                        {expandedDates[dayGroup.date] && (
                          <div className="border-t border-wt-border">
                            {dayGroup.events.map((event, idx) => (
                              <div key={event.id || idx} className="p-4 border-b border-wt-border/50 last:border-b-0">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    {event.type === 'journey_message' && (
                                      <div className="p-1.5 bg-purple-500/20 rounded">
                                        <LinkIcon className="h-4 w-4 text-purple-400" />
                                      </div>
                                    )}
                                    {event.type === 'order' && (
                                      <div className="p-1.5 bg-green-500/20 rounded">
                                        <ShoppingBag className="h-4 w-4 text-green-400" />
                                      </div>
                                    )}
                                    {event.type === 'inbox_message' && (
                                      <div className="p-1.5 bg-blue-500/20 rounded">
                                        <MessageSquare className="h-4 w-4 text-blue-400" />
                                      </div>
                                    )}
                                    {(event.type === 'credit_added' || event.type === 'credit_used') && (
                                      <div className={`p-1.5 rounded ${event.type === 'credit_added' ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
                                        <CreditCard className={`h-4 w-4 ${event.type === 'credit_added' ? 'text-emerald-400' : 'text-amber-400'}`} />
                                      </div>
                                    )}
                                    {event.type === 'note' && (
                                      <div className="p-1.5 bg-yellow-500/20 rounded">
                                        <Edit className="h-4 w-4 text-yellow-400" />
                                      </div>
                                    )}
                                    <div>
                                      <p className="text-wt-text-primary font-medium">{event.title}</p>
                                      {event.subtitle && (
                                        <p className="text-wt-text-muted text-xs">{event.subtitle}</p>
                                      )}
                                      {event.journey_name && !event.subtitle && (
                                        <a href={`/marketing/journeys/${event.journey_id}`} className="text-blue-400 text-xs flex items-center gap-1 hover:underline">
                                          <LinkIcon className="h-3 w-3" />
                                          {event.journey_name}
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs border-wt-border">
                                      {event.channel}
                                    </Badge>
                                    <span className="text-wt-text-muted text-xs">
                                      {event.timestamp ? new Date(event.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Event details */}
                                {event.type === 'journey_message' && event.details && (
                                  <div className="bg-wt-paper rounded-lg p-3 mt-2">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                      <div>
                                        <p className="text-wt-text-muted">From</p>
                                        <p className="text-wt-text-primary">{event.details.from || '-'}</p>
                                      </div>
                                      <div>
                                        <p className="text-wt-text-muted">To</p>
                                        <p className="text-wt-text-primary">{event.details.to || '-'}</p>
                                      </div>
                                      <div>
                                        <p className="text-wt-text-muted">Sent At</p>
                                        <p className="text-wt-text-primary">
                                          {event.details.sent_at ? new Date(event.details.sent_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-wt-text-muted">Delivered At</p>
                                        <p className="text-wt-text-primary">
                                          {event.details.delivered_at ? new Date(event.details.delivered_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-wt-text-muted">Read At</p>
                                        <p className="text-wt-text-primary">
                                          {event.details.read_at ? new Date(event.details.read_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-wt-text-muted">Clicked At</p>
                                        <p className="text-wt-text-primary">
                                          {event.details.clicked_at ? new Date(event.details.clicked_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}
                                
                                {event.type === 'order' && event.details && (
                                  <div className="bg-wt-paper rounded-lg p-3 mt-2 flex items-center justify-between">
                                    <div className="text-xs">
                                      <span className="text-wt-text-muted">Order </span>
                                      <span className="text-wt-text-primary font-medium">#{event.details.order_number}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-wt-text-primary font-medium">{formatCurrency(event.details.total_amount || 0)}</span>
                                      <Badge className={event.details.fulfillment_status === 'fulfilled' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                                        {event.details.fulfillment_status || event.details.status || 'pending'}
                                      </Badge>
                                    </div>
                                  </div>
                                )}
                                
                                {event.type === 'inbox_message' && event.details && (
                                  <div className="bg-wt-paper rounded-lg p-3 mt-2">
                                    <p className="text-xs text-wt-text-muted mb-1">{event.details.direction === 'inbound' ? 'From customer:' : 'Sent:'}</p>
                                    <p className="text-sm text-wt-text-primary">{event.details.content || '[Media]'}</p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-wt-text-muted text-center py-8">No activity found</p>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPage;
